import { useState, useEffect, useMemo } from 'react';
import { Package, RefreshCcw, Eye, ShoppingBag, Truck, ArrowUpDown, ChevronLeft, ChevronRight, FileText, Loader2 } from 'lucide-react';
import { getOrders, BaselinkerOrder, determineOrderState, getOrderStatusColor } from '../../services/baselinkerService';
import { collection, query, where, getDocs, addDoc, Timestamp, updateDoc, doc, getDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import OrderSummaryModal from '../../components/orders/OrderSummaryModal';
import SyncProgressModal from '../../components/orders/SyncProgressModal';

interface Product {
  id: string;
  name: string;
  sku: string;
  baselinker_id: string;
  stockQuantity: number;
  companyId: string;
  ean: string;
}

interface StoredOrder extends BaselinkerOrder {
  firebaseId?: string;
  syncedAt: number;
  order_source?: string;
  order_source_id?: number;
  order_page?: string;
  history?: {
    timestamp: number;
    changes: Record<string, any>;
  }[];
}

type SortConfig = {
  key: keyof StoredOrder | '';
  direction: 'asc' | 'desc';
}

interface Filters {
  orderId: string;
  shopOrderId: string;
  source: string;
  status: string;
  dateFrom: string;
  dateTo: string;
}

export default function ExternalOrders() {
  const { currentUser } = useAuth();

  const [orders, setOrders] = useState<StoredOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<StoredOrder | null>(null);
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedOrderUrl, setSelectedOrderUrl] = useState('');
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncStats, setSyncStats] = useState<{ added: number; updated: number; duplicatesRemoved: number; }>({
    added: 0,
    updated: 0,
    duplicatesRemoved: 0
  });
  const [filters, setFilters] = useState<Filters>({
    orderId: '',
    shopOrderId: '',
    source: '',
    dateFrom: '',
    dateTo: '',
    status: ''
  });

  useEffect(() => {
    const init = async () => {
      if (!currentUser?.companyId) {
        setError('Usuário não encontrado');
        return;
      }

      setLoading(true);
      try {
        // 1. Primeiro carrega os produtos
        const userProducts = await loadProducts();
        
        // 2. Busca ordens existentes do Firebase primeiro
        const ordersRef = collection(db, 'warehouse_orders');
        const allOrdersSnapshot = await getDocs(ordersRef);
        const existingOrders = allOrdersSnapshot.docs.map(doc => ({
          ...doc.data(),
          firebaseId: doc.id
        })) as StoredOrder[];
        
        // 3. Busca novas ordens do Baselinker
        const newOrders = await getOrders();
        
        // 4. Combina ordens existentes com novas
        const allOrders = [...existingOrders];
        
        // 5. Adiciona novas ordens que não existem
        for (const newOrder of newOrders) {
          const existingOrder = existingOrders.find(
            order => order.order_id === newOrder.order_id
          );
          
          if (!existingOrder) {
            allOrders.push({
              ...newOrder,
              firebaseId: '', // Será preenchido ao salvar no Firebase
              status: determineOrderState(newOrder)
            });
          }
        }

        // 6. Filtra ordens pelos produtos do usuário
        const filteredByProducts = filterOrders(allOrders, userProducts);

        // 7. Ordena as ordens por data (mais recente primeiro)
        const sortedOrders = filteredByProducts.sort((a, b) => {
          const dateA = new Date(a.date_add * 1000);
          const dateB = new Date(b.date_add * 1000);
          return dateB.getTime() - dateA.getTime();
        });

        setOrders(sortedOrders);
        setProducts(userProducts);

        // 8. Salva última sincronização
        const now = Date.now();
        setLastSync(now);
        try {
          localStorage.setItem('lastSync', now.toString());
        } catch (storageError) {
          // Erro ao salvar no localStorage
        }
      } catch (error) {
        setError('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [currentUser]);

  const handleSync = async () => {
    if (!currentUser?.companyId) {
      toast.error('Usuário não encontrado');
      return;
    }

    setSyncModalOpen(true);
    setSyncStats({ added: 0, updated: 0, duplicatesRemoved: 0 });
    setLoading(true);

    try {
      // 1. Primeiro carrega os produtos
      const userProducts = await loadProducts();
      
      // 2. Busca ordens existentes do Firebase primeiro
      const ordersRef = collection(db, 'warehouse_orders');
      const allOrdersSnapshot = await getDocs(ordersRef);
      const existingOrders = allOrdersSnapshot.docs.map(doc => ({
        ...doc.data(),
        firebaseId: doc.id
      })) as StoredOrder[];
      
      // 3. Busca novas ordens do Baselinker
      const newOrders = await getOrders();
      
      // 4. Filtra apenas as novas ordens que contêm produtos do usuário
      const relevantNewOrders = filterOrders(newOrders, userProducts);
      
      // 5. Combina ordens existentes com novas
      const allOrders = [...existingOrders];
      
      // 6. Adiciona/atualiza apenas as ordens relevantes
      for (const newOrder of relevantNewOrders) {
        const existingOrder = existingOrders.find(
          order => order.order_id === newOrder.order_id
        );
        
        if (!existingOrder) {
          // Adiciona nova ordem ao Firebase
          const orderRef = await addDoc(collection(db, 'warehouse_orders'), {
            ...newOrder,
            syncedAt: Date.now()
          });
          
          allOrders.push({
            ...newOrder,
            firebaseId: orderRef.id,
            syncedAt: Date.now()
          });
          
          setSyncStats(prev => ({
            ...prev,
            added: prev.added + 1
          }));
        } else {
          // Atualiza ordem existente se necessário
          const hasChanges = JSON.stringify(newOrder) !== JSON.stringify(existingOrder);
          
          if (hasChanges) {
            const orderRef = doc(db, 'warehouse_orders', existingOrder.firebaseId);
            await updateDoc(orderRef, {
              ...newOrder,
              syncedAt: Date.now()
            });
            
            const orderIndex = allOrders.findIndex(
              order => order.firebaseId === existingOrder.firebaseId
            );
            
            if (orderIndex !== -1) {
              allOrders[orderIndex] = {
                ...newOrder,
                firebaseId: existingOrder.firebaseId,
                syncedAt: Date.now()
              };
            }
            
            setSyncStats(prev => ({
              ...prev,
              updated: prev.updated + 1
            }));
          }
        }
      }

      // 7. Filtra todas as ordens pelos produtos do usuário
      const filteredByProducts = filterOrders(allOrders, userProducts);

      // 8. Ordena as ordens por data (mais recente primeiro)
      const sortedOrders = filteredByProducts.sort((a, b) => {
        const dateA = new Date(a.date_add * 1000);
        const dateB = new Date(b.date_add * 1000);
        return dateB.getTime() - dateA.getTime();
      });

      setOrders(sortedOrders);
      setProducts(userProducts);

      // 9. Salva última sincronização
      const now = Date.now();
      setLastSync(now);
      try {
        localStorage.setItem('lastSync', now.toString());
      } catch (storageError) {
        // Erro ao salvar no localStorage
      }
    } catch (error) {
      setError('Erro ao sincronizar ordens');
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = (orders: StoredOrder[], userProducts: Record<string, Product>) => {
    return orders.filter(order => {
      // Verifica se algum produto do pedido corresponde aos produtos do usuário
      return order.products.some(orderProduct => {
        return Object.values(userProducts).some(userProduct => {
          // Verifica correspondência por SKU
          const skuMatch = userProduct.sku && orderProduct.sku && 
                         userProduct.sku.trim().toLowerCase() === orderProduct.sku.trim().toLowerCase();

          // Verifica correspondência por EAN
          const eanMatch = userProduct.ean && orderProduct.ean && 
                         userProduct.ean.trim().toLowerCase() === orderProduct.ean.trim().toLowerCase();

          return skuMatch || eanMatch;
        });
      });
    });
  };

  const loadProducts = async () => {
    if (!currentUser?.companyId) {
      return {};
    }

    try {
      const productsRef = collection(db, 'products');
      const q = query(productsRef, where('companyId', '==', currentUser.companyId));
      const userProductsSnapshot = await getDocs(q);
      
      const productsMap: Record<string, Product> = {};
      userProductsSnapshot.docs.forEach(doc => {
        const productData = doc.data() as Product;
        productsMap[doc.id] = {
          ...productData,
          id: doc.id
        };
      });

      return productsMap;
    } catch (error) {
      setError('Erro ao carregar produtos');
      return {};
    }
  };

  const canSync = () => {
    return true;
  };

  const getRemainingTime = () => {
    const lastOrderSync = localStorage.getItem('lastOrderSync');
    if (!lastOrderSync) return '';

    const currentTime = new Date().getTime();
    const timeSinceLastSync = currentTime - parseInt(lastOrderSync, 10);
    const remainingTime = 5 * 60 * 1000 - timeSinceLastSync;
    
    if (remainingTime <= 0) return '';
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const syncOrders = async () => {
    if (!currentUser?.companyId) return;
    
    setLoading(true);
    setSyncStats({ added: 0, updated: 0, duplicatesRemoved: 0 });
    setSyncModalOpen(true);

    try {
      const ordersRef = collection(db, 'warehouse_orders');
      const q = query(ordersRef, where('companyId', '==', currentUser.companyId));
      const querySnapshot = await getDocs(q);
      
      const orderMap = new Map<string, { order: StoredOrder; docs: { id: string; syncedAt: number }[] }>();
      
      querySnapshot.docs.forEach(doc => {
        const order = doc.data() as StoredOrder;
        if (!order?.order_id) return;
        
        const orderId = order.order_id.toString();
        if (!orderMap.has(orderId)) {
          orderMap.set(orderId, { order, docs: [{ id: doc.id, syncedAt: order.syncedAt || 0 }] });
        } else {
          orderMap.get(orderId)?.docs.push({ id: doc.id, syncedAt: order.syncedAt || 0 });
        }
      });

      const batch = writeBatch(db);
      let duplicatesRemoved = 0;

      for (const [orderId, { docs }] of orderMap) {
        if (docs.length > 1) {
          docs.sort((a, b) => b.syncedAt - a.syncedAt);
          
          for (let i = 1; i < docs.length; i++) {
            batch.delete(doc(db, 'warehouse_orders', docs[i].id));
            duplicatesRemoved++;
          }
        }
      }

      const newOrders = await getOrders();
      
      const currentProducts = await loadProducts();
      
      const filteredNewOrders = filterOrders(newOrders, currentProducts);
      
      let added = 0;
      let updated = 0;

      for (const order of filteredNewOrders) {
        if (!order?.order_id) continue;
        
        const orderId = order.order_id.toString();
        const existingOrderData = orderMap.get(orderId);
        
        const orderData = {
          ...order,
          companyId: currentUser.companyId,
          syncedAt: Date.now(),
          order_page: order.order_page || null,
          history: [{
            timestamp: Date.now(),
            changes: {
              order_status_id: order.order_status_id,
              pick_state: order.pick_state,
              pack_state: order.pack_state
            }
          }]
        };

        if (existingOrderData) {
          const mostRecentDoc = existingOrderData.docs[0];
          if (mostRecentDoc) {
            batch.update(doc(db, 'warehouse_orders', mostRecentDoc.id), {
              ...orderData,
              history: [
                ...(existingOrderData.order.history || []),
                {
                  timestamp: Date.now(),
                  changes: {
                    order_status_id: order.order_status_id,
                    pick_state: order.pick_state,
                    pack_state: order.pack_state
                  }
                }
              ]
            });
            updated++;
          }
        } else {
          batch.set(doc(ordersRef), orderData);
          added++;
        }
      }

      await batch.commit();

      // Atualiza os estados com os dados mais recentes
      setProducts(currentProducts);
      setOrders(filteredNewOrders);
      
      setSyncStats({
        added,
        updated,
        duplicatesRemoved
      });

      // Atualiza o cache
      try {
        localStorage.setItem('cachedOrders', JSON.stringify(filteredNewOrders));
        localStorage.setItem('lastOrderSync', Date.now().toString());
        setLastSync(Date.now());
      } catch (storageError) {
        // Erro ao salvar no localStorage
      }

      if (duplicatesRemoved > 0) {
        toast.success(`Sincronização concluída. ${duplicatesRemoved} duplicatas removidas.`);
      } else {
        toast.success('Sincronização concluída com sucesso.');
      }

    } catch (error) {
      setError('Erro ao sincronizar ordens');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: keyof StoredOrder) => {
    let direction: 'asc' | 'desc' = 'asc';
    
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });
    
    const sortedOrders = [...orders].sort((a, b) => {
      if (key === 'date_add') {
        return direction === 'asc' 
          ? new Date(a[key]).getTime() - new Date(b[key]).getTime()
          : new Date(b[key]).getTime() - new Date(a[key]).getTime();
      }
      
      if (key === 'payment_done') {
        return direction === 'asc'
          ? a[key] - b[key]
          : b[key] - a[key];
      }

      const aValue = String(a[key]).toLowerCase();
      const bValue = String(b[key]).toLowerCase();
      
      return direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    setOrders(sortedOrders);
  };

  const formatTimestamp = (timestamp: number) => {
    return format(new Date(timestamp * 1000), "dd/MM/yy HH:mm", { locale: pt });
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      orderId: '',
      shopOrderId: '',
      source: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const getSortIcon = (key: keyof StoredOrder) => {
    if (sortConfig.key === key) {
      return (
        <span className={`inline-block transition-transform duration-200 ${
          sortConfig.direction === 'desc' ? 'transform rotate-180' : ''
        }`}>
          ▲
        </span>
      );
    }
    return <ArrowUpDown className="inline w-4 h-4 opacity-50" />;
  };

  const renderSortableHeader = (key: keyof StoredOrder, label: string) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50"
      onClick={() => handleSort(key)}
    >
      <div className="flex items-center gap-1">
        {label}
        {getSortIcon(key)}
      </div>
    </th>
  );

  const filteredOrders = useMemo(() => {
    let result = [...orders]; // Começa com todas as ordens

    // Filtro por Nº da Encomenda
    if (filters.orderId) {
      result = result.filter(order => 
        order.order_id?.toString().toLowerCase().includes(filters.orderId.toLowerCase())
      );
    }

    // Filtro por Nº da Loja
    if (filters.shopOrderId) {
      result = result.filter(order => {
        // Se shop_order_id for 0, usamos external_order_id
        const shopId = order.shop_order_id === 0 ? 
                      order.external_order_id?.toString() : 
                      order.shop_order_id?.toString();
        
        return shopId?.toLowerCase().includes(filters.shopOrderId.toLowerCase());
      });
    }

    // Filtro por Origem
    if (filters.source) {
      result = result.filter(order => 
        order.order_source?.toLowerCase() === filters.source.toLowerCase()
      );
    }

    // Filtro por Estado
    if (filters.status) {
      result = result.filter(order => {
        const orderState = determineOrderState(order);
        return orderState.toLowerCase() === filters.status.toLowerCase();
      });
    }

    // Filtro por Data de Início
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter(order => {
        const orderDate = new Date(order.date_add * 1000);
        return orderDate >= fromDate;
      });
    }

    // Filtro por Data de Fim
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter(order => {
        const orderDate = new Date(order.date_add * 1000);
        return orderDate <= toDate;
      });
    }

    // Ordenação
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        if (sortConfig.key === 'date') {
          const dateA = new Date(a.date * 1000).getTime();
          const dateB = new Date(b.date * 1000).getTime();
          return sortConfig.direction === 'asc'
            ? dateA - dateB
            : dateB - dateA;
        }

        const valueA = a[sortConfig.key] || '';
        const valueB = b[sortConfig.key] || '';

        if (valueA < valueB) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [orders, filters, sortConfig]);

  const orderSources = useMemo(() => {
    const sources = new Set<string>();
    filteredOrders.forEach(order => {
      if (order.order_source) {
        sources.add(order.order_source);
      }
    });
    return Array.from(sources);
  }, [filteredOrders]);

  const ITEMS_PER_PAGE = 20;
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const formatHistoryChanges = (changes: Record<string, any>) => {
    if (!changes) return 'Sem detalhes de alteração';

    if (changes.products && Array.isArray(changes.products)) {
      return (
        <div>
          <h4 className="font-semibold mb-2">Alterações nos Produtos</h4>
          {changes.products.map((productChange, index) => (
            <div key={index} className="mb-2 p-2 bg-gray-50 rounded">
              <p className="font-medium">{productChange.name || 'Produto sem nome'}</p>
              <ul className="list-disc list-inside text-sm text-gray-700">
                <li>Quantidade: {productChange.quantity || 'N/A'}</li>
                <li>EAN: {productChange.ean || 'Não especificado'}</li>
                <li>Preço: {productChange.price_brutto ? `€${productChange.price_brutto.toFixed(2)}` : 'N/A'}</li>
                {productChange.attributes && (
                  <li>Atributos: {productChange.attributes}</li>
                )}
                {productChange.location && (
                  <li>Localização: {productChange.location}</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div>
        <h4 className="font-semibold mb-2">Detalhes da Alteração</h4>
        <ul className="list-disc list-inside text-sm text-gray-700">
          {Object.entries(changes).map(([key, value]) => (
            <li key={key}>
              {key}: {JSON.stringify(value)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const FilterBar = () => (
    <div className="mb-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Nº da Encomenda</label>
        <input
          type="text"
          value={filters.orderId}
          onChange={(e) => handleFilterChange('orderId', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          placeholder="Filtrar por Nº"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700">Nº da Loja</label>
        <input
          type="text"
          value={filters.shopOrderId}
          onChange={(e) => handleFilterChange('shopOrderId', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          placeholder="Filtrar por Nº da Loja"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Origem</label>
        <select
          value={filters.source}
          onChange={(e) => handleFilterChange('source', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
        >
          <option value="">Todas</option>
          {Array.from(new Set(orders.map(order => order.order_source))).map(source => (
            <option key={source} value={source}>{source}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Estado</label>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
        >
          <option value="">Todos</option>
          {Array.from(new Set(orders.map(order => determineOrderState(order)))).map(status => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Data Inicial</label>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Data Final</label>
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-semibold text-gray-900">Pedidos Externos</h1>
          <button
            onClick={() => {
              setSyncModalOpen(true);
              handleSync();
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCcw className="h-4 w-4 mr-2" />
            )}
            Sincronizar
          </button>
        </div>

        <button
          onClick={clearFilters}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          Limpar Filtros
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      <FilterBar />

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] whitespace-nowrap">
                  Nº da Encomenda
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] whitespace-nowrap">
                  Nº da Loja
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] whitespace-nowrap">
                  Origem
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px] whitespace-nowrap">
                  Data
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px] whitespace-nowrap">
                  Cliente
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px] whitespace-nowrap">
                  Valor
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] whitespace-nowrap">
                  Estado
                </th>
                <th scope="col" className="relative px-3 py-3 min-w-[80px]">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedOrders.map((order, index) => (
                <tr key={`${order.order_id}-${order.firebaseId || index}`} className="hover:bg-gray-50">
                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{order.order_id}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    #{order.shop_order_id || order.external_order_id || 'Manual'}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                      {order.order_source || 'shop'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimestamp(order.date_add)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {order.delivery_fullname}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                    €{order.payment_done.toFixed(2)}
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap">
                    <span
                      className="rounded-full px-3 py-1 text-xs font-medium"
                      style={{
                        backgroundColor: getOrderStatusColor(order.order_status_id),
                        color: getOrderStatusColor(order.order_status_id) === '#000000' ? '#ffffff' : '#000000'
                      }}
                    >
                      {determineOrderState(order)}
                    </span>
                  </td>
                  <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {order.order_page && (
                        <button
                          onClick={() => {
                            setSelectedOrderUrl(order.order_page || '');
                            setShowSummaryModal(true);
                          }}
                          className="text-primary hover:text-primary-dark"
                          title="Ver Resumo"
                        >
                          <FileText className="h-5 w-5" />
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-primary hover:text-primary-dark"
                        title="Ver Detalhes"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    {loading ? 'Carregando ordens...' : 'Nenhuma ordem encontrada'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Anterior
            </button>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Próxima
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                A mostrar <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> a{' '}
                <span className="font-medium">
                  {Math.min(currentPage * ITEMS_PER_PAGE, filteredOrders.length)}
                </span>{' '}
                de <span className="font-medium">{filteredOrders.length}</span> encomendas
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Paginação">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                    return false;
                  })
                  .map((page, i, array) => {
                    if (i > 0 && array[i - 1] !== page - 1) {
                      return (
                        <span
                          key={`ellipsis-${page}`}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => paginate(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-primary border-primary text-white'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-[1200px] h-[800px] flex flex-col">
            <div className="bg-primary text-white p-4 rounded-t-lg flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Package size={24} />
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold">Pedido #{selectedOrder.order_id}</h2>
                  <span className="px-2 py-1 text-xs font-medium bg-white/20 text-white rounded-full">
                    {selectedOrder.shop_order_id === 0 && !selectedOrder.external_order_id ? 'Manual' : selectedOrder.order_source || 'worten'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 p-6 grid grid-cols-12 gap-6 overflow-auto">
              <div className="col-span-8 space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingBag className="text-amber-500" size={20} />
                      <h3 className="font-medium text-gray-900">Pedido na Loja</h3>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">
                      #{selectedOrder.shop_order_id !== 0 ? selectedOrder.shop_order_id : 
                        selectedOrder.external_order_id ? selectedOrder.external_order_id : 
                        'Manual'}
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="text-blue-500" size={20} />
                      <h3 className="font-medium text-gray-900">Método de Envio</h3>
                    </div>
                    <p className="text-sm font-medium text-blue-700">{selectedOrder.delivery_method}</p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="font-medium text-gray-900">Valor Total</h3>
                    </div>
                    <p className="text-2xl font-bold text-green-700">€{selectedOrder.payment_done.toFixed(2)}</p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                    <h3 className="font-medium text-gray-900">Produtos</h3>
                  </div>
                  <div className="p-4">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                          <th className="text-center text-xs font-medium text-gray-500 uppercase w-20">Qtd</th>
                          <th className="text-right text-xs font-medium text-gray-500 uppercase w-24">Preço Unit.</th>
                          <th className="text-right text-xs font-medium text-gray-500 uppercase w-24">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedOrder.products.map((product, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="py-3">
                              <div className="font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500">SKU: {product.sku}</div>
                            </td>
                            <td className="py-3 text-center">{product.quantity}</td>
                            <td className="py-3 text-right">€{(product.price_brutto / product.quantity).toFixed(2)}</td>
                            <td className="py-3 text-right font-medium">€{product.price_brutto.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={2} />
                          <td className="py-3 text-right font-medium">Subtotal:</td>
                          <td className="py-3 text-right font-medium">€{(selectedOrder.payment_done - selectedOrder.delivery_price).toFixed(2)}</td>
                        </tr>
                        <tr>
                          <td colSpan={2} />
                          <td className="py-3 text-right font-medium">Envio:</td>
                          <td className="py-3 text-right font-medium">€{selectedOrder.delivery_price.toFixed(2)}</td>
                        </tr>
                        <tr className="border-t-2 border-gray-200">
                          <td colSpan={2} />
                          <td className="py-3 text-right font-medium">Total:</td>
                          <td className="py-3 text-right font-bold">€{selectedOrder.payment_done.toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>

              <div className="col-span-4 space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                    <h3 className="font-medium text-gray-900">Cliente</h3>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="font-medium text-gray-900">{selectedOrder.delivery_fullname}</p>
                    <div className="text-sm text-gray-600">
                      <p className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {selectedOrder.email}
                      </p>
                      <p className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {selectedOrder.phone}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                    <h3 className="font-medium text-gray-900">Endereço de Entrega</h3>
                  </div>
                  <div className="p-4 space-y-1">
                    <p className="font-medium text-gray-900">{selectedOrder.delivery_address}</p>
                    <p className="text-gray-600">{selectedOrder.delivery_city}</p>
                    <p className="text-gray-600">{selectedOrder.delivery_postcode}</p>
                    <p className="text-gray-600">{selectedOrder.delivery_country}</p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                    <h3 className="font-medium text-gray-900">Detalhes do Envio</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Preço do Envio:</span>
                      <span className="font-medium">€{selectedOrder.delivery_price.toFixed(2)}</span>
                    </div>
                    {selectedOrder.delivery_package_module && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Módulo:</span>
                        <span className="font-medium">{selectedOrder.delivery_package_module}</span>
                      </div>
                    )}
                    {selectedOrder.delivery_package_nr && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Tracking da Envio:</p>
                        <a
                          href={`https://portal.shippix.pt/pt/tracking?tracking=${selectedOrder.delivery_package_nr}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium bg-gray-50 p-2 rounded text-sm break-all hover:bg-gray-100 transition-colors block text-primary"
                        >
                          {selectedOrder.delivery_package_nr}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSummaryModal && selectedOrderUrl && (
        <OrderSummaryModal
          isOpen={showSummaryModal}
          onClose={() => setShowSummaryModal(false)}
          orderUrl={selectedOrderUrl}
        />
      )}
      <SyncProgressModal
        isOpen={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        syncStats={syncStats}
        isLoading={loading}
      />
    </div>
  );
}
